import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Modal, Spin, Tag } from 'antd';
import { ApiOutlined, CheckOutlined, GithubOutlined, SlackOutlined } from '@ant-design/icons';
import colorTokens from '../../../constants/design_system/colors';
import { OrganizationAccessRoles } from '../../../common/auth';
import { useOrgRoleInfo } from '../../BaseIndex/useOrgRoleInfo';
import { rememberInstallOriginOrg } from '../../../common/githubInstallOrigin';
import {
  useConnectGithubMutation,
  useDisconnectInstallationMutation,
  useGetGithubInstallations,
} from '../../../serverState/github';
import {
  useConnectSlackMutation,
  useDisconnectSlackMutation,
  useGetSlackInstallation,
} from '../../../serverState/slack';
import RepositoryMappingTable from './RepositoryMappingTable';

/**
 * Organization Settings → Integrations.
 *
 * `organizationId` here is the NUMERIC id from the active organization, never the
 * publicId in the URL - the server's organizationGuard reads a number, and passing the
 * publicId produces a 403 that looks like a permissions bug.
 *
 * DESIGN: page shell matches its Settings siblings (`Settings/Organization`,
 * `Settings/MyProfile`) - `px-12 max-w-5xl mx-auto`, a `text-brand-grey-dark text-xl`
 * title, bordered cards. In the design-system scale `neutral` runs light → dark and
 * inverts under `data-theme="dark"`, so headings take `text-brand-grey-dark` and body copy
 * `text-neutral-900`; `text-neutral-700` is a border tone, not a text tone. Tailwind
 * preflight is disabled repo-wide, so bordered elements need an explicit `border-solid`.
 *
 * Actions are antd `Button`s rather than `BaseButton`: `BaseButton` renders a `<div>` with
 * no role and no native `disabled`, which would leave "Connect GitHub" - the entry point to
 * the whole install flow - unreachable by keyboard, and its `flex` root stretches to the
 * full width of any non-flex parent.
 */
/**
 * The design system's `Label - Status`: a 4px chip, 12px/16 text, no border, and a
 * background/foreground pair per tone. antd's `Tag` is the wrong shape for it - it carries
 * its own border, its own 2px radius and its own palette - so this is a span.
 *
 * Tones come from the Tailwind scale rather than the raw hexes in the design, because
 * every token there resolves to a CSS variable that inverts under `data-theme="dark"`.
 * `orange` has no frame of its own: a suspended installation is a state the design does
 * not draw, and yellow is the scale's warning tone.
 */
const STATUS_TONES = {
  neutral: 'bg-neutral-200 text-neutral-800',
  green: 'bg-green-100 text-green-600',
  orange: 'bg-yellow-100 text-yellow-600',
};

const StatusLabel = ({ tone, children }: { tone: keyof typeof STATUS_TONES; children: React.ReactNode }) => (
  <span className={`flex items-center gap-1 rounded px-1 text-xs font-normal leading-4 ${STATUS_TONES[tone]}`}>
    {children}
  </span>
);

export default function IntegrationsSettings() {
  const { activeOrganization, organizationRole, roleResolved } = useOrgRoleInfo();
  const organizationId = activeOrganization?.id ?? null;
  // useOrgRoleInfo resolves organizationRole in a useEffect, so it is `undefined` on
  // first render - but it is ALSO `undefined` in real, settled states: no active org,
  // no matching access row, or the lookup rejecting (it only logs, never surfaces an
  // error state). Inferring "still loading" from `organizationRole === undefined`
  // conflates those and renders an indefinite spinner for a settled non-admin/error
  // state. Use the hook's own settled flag instead.
  const roleLoading = !roleResolved;
  const isAdmin = organizationRole === OrganizationAccessRoles.ADMIN;

  const installations = useGetGithubInstallations(organizationId);
  const connectMutation = useConnectGithubMutation();
  const disconnectMutation = useDisconnectInstallationMutation();

  const slackInstallation = useGetSlackInstallation(organizationId);
  const connectSlackMutation = useConnectSlackMutation();
  const disconnectSlackMutation = useDisconnectSlackMutation();

  const [error, setError] = useState<string | null>(null);
  const [pendingDisconnect, setPendingDisconnect] = useState<string | null>(null);
  const [slackDisconnectOpen, setSlackDisconnectOpen] = useState(false);

  const onConnect = async () => {
    if (!organizationId) return;
    setError(null);
    try {
      const installUrl = await connectMutation.mutateAsync(organizationId);
      // Leave a hint for the callback screen so it does not ask which organization
      // this was started from. A hint only - see common/githubInstallOrigin.
      rememberInstallOriginOrg(organizationId);
      // A full navigation, not a router push: the next hop is github.com.
      window.location.href = installUrl;
    } catch {
      setError('Could not start the GitHub installation. Please try again.');
    }
  };

  const onDisconnect = async () => {
    if (!organizationId || !pendingDisconnect) return;
    setError(null);
    try {
      await disconnectMutation.mutateAsync({
        organizationId,
        installationId: pendingDisconnect,
      });
    } catch {
      setError('Could not disconnect that installation. Please try again.');
    } finally {
      setPendingDisconnect(null);
    }
  };

  const onConnectSlack = async () => {
    if (!organizationId) return;
    setError(null);
    try {
      const installUrl = await connectSlackMutation.mutateAsync(organizationId);
      // A full navigation, not a router push: the next hop is slack.com.
      window.location.href = installUrl;
    } catch {
      setError('Could not start the Slack installation. Please try again.');
    }
  };

  const onDisconnectSlack = async () => {
    if (!organizationId) return;
    setError(null);
    try {
      await disconnectSlackMutation.mutateAsync(organizationId);
    } catch {
      setError('Could not disconnect Slack. Please try again.');
    } finally {
      setSlackDisconnectOpen(false);
    }
  };

  const page = (children: React.ReactNode) => (
    <div className="mx-auto w-full max-w-5xl px-12">
      <div className="mb-6 mt-8 text-xl font-semibold leading-7 text-brand-grey-dark">Integrations</div>
      {children}
    </div>
  );

  if (roleLoading) {
    return page(
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  if (!isAdmin) {
    return page(<Alert type="info" showIcon={true} message="Only organization admins can manage integrations." />);
  }

  const connected = installations.data ?? [];
  const slackSettled = !slackInstallation.isLoading && !slackInstallation.isError;
  const slackConnection = slackInstallation.data;
  const slackConnected = slackSettled && slackConnection?.connected === true ? slackConnection : null;
  const settled = !installations.isLoading && !installations.isError;
  // The design's single-installation layout: the account named in the badge, Disconnect
  // beside it. `organizationId` has no unique constraint on GithubInstallation, so more
  // than one is possible and falls back to the per-installation list further down.
  const soleInstallation = settled && connected.length === 1 ? connected[0] : null;

  return page(
    <>
      <p className="m-0 -mt-3 mb-6 text-sm leading-relaxed text-neutral-900">
        Bring the tools your team already uses into Taskonio.
      </p>

      {error && <Alert className="mb-4" type="error" showIcon={true} message={error} />}

      <section data-testid="github-card" className="rounded-lg border border-solid border-neutral-300 p-6">
        {/* One 36px row, centred in the card's 24px padding - the design's 84px header.
            The logo box is 32px with a 24px mark, the title is 14/20 and the description
            12/16 sits directly under it with no gap of its own. */}
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border-[1.333px] border-solid border-neutral-300 bg-neutral-200">
            <GithubOutlined className="text-2xl text-brand-grey-dark" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-sm font-medium leading-5 text-brand-grey-dark">GitHub</h2>
              {/* The account is named in the badge rather than on a row of its own below.
                  Only when there is exactly one: `organizationId` carries no unique
                  constraint, so an organization CAN hold several installations, and one
                  badge cannot name them all. The list below is the fallback for that. */}
              {settled && soleInstallation && (
                <StatusLabel tone="green">
                  <CheckOutlined className="text-[12px]" />
                  <span>
                    Connected to <span className="font-semibold">{soleInstallation.accountLogin}</span>
                  </span>
                </StatusLabel>
              )}
              {settled && connected.length > 1 && (
                <StatusLabel tone="green">
                  <CheckOutlined className="text-[12px]" />
                  Connected
                </StatusLabel>
              )}
              {settled && connected.length === 0 && <StatusLabel tone="neutral">Not Connected</StatusLabel>}
              {settled && soleInstallation?.suspended && <StatusLabel tone="orange">Suspended on GitHub</StatusLabel>}
            </div>
            <p className="m-0 text-xs leading-4 text-brand-grey-dark">
              Link pull requests to tasks and mirror pull request discussion into Taskonio.
            </p>
          </div>

          {settled && connected.length === 0 && (
            <Button
              type="primary"
              // `font-semibold` is the design's Button/14 SemiBold. antd renders every
              // button at 400, so this one CTA is heavier than the rest of the app's.
              className="shrink-0 font-semibold"
              icon={<ApiOutlined />}
              disabled={!organizationId}
              loading={connectMutation.isPending}
              // The design's button is blue/600, whose hex is this project's own
              // `--color-blue-600` exactly - not the Taskonio green that antd's primary
              // resolves to here. It is the same colour `BaseButton type="accent"` uses,
              // so a blue call to action is a sanctioned pattern; BaseButton itself is not
              // an option because it renders a <div>, and this button is the entry point
              // to the whole install flow (see the note above).
              //
              // Inline because antd's own `.ant-btn-primary` background wins on
              // specificity against a class. The value is still a CSS variable, so it
              // inverts under `data-theme="dark"`. Dropped when disabled, or it would
              // paint over antd's disabled palette.
              style={
                organizationId
                  ? { backgroundColor: colorTokens.blue[600], borderColor: colorTokens.blue[600] }
                  : undefined
              }
              onClick={() => void onConnect()}
            >
              Connect for your organization
            </Button>
          )}

          {/* Alongside the badge that names what it disconnects, instead of on a row of
              its own. Several installations keep the per-row buttons below. */}
          {settled && soleInstallation && (
            <Button
              danger={true}
              className="shrink-0"
              disabled={disconnectMutation.isPending}
              onClick={() => setPendingDisconnect(soleInstallation.installationId)}
            >
              Disconnect
            </Button>
          )}
        </div>

        {installations.isLoading ? (
          <div className="mt-6 flex justify-center">
            <Spin />
          </div>
        ) : installations.isError ? (
          <div className="mt-6 border-0 border-t border-solid border-neutral-300 pt-6">
            <p className="m-0 text-sm text-neutral-900">
              Could not load GitHub connection status for this organization. Please try again.
            </p>
            <Button className="mt-3" onClick={() => void installations.refetch()}>
              Retry
            </Button>
          </div>
        ) : connected.length === 0 ? null : ( // a divider was the card explaining itself twice. // the description says what connecting buys, so a paragraph repeating both under // Nothing. The design's zero state is one 84px row: the badge says the state and
          <>
            {soleInstallation ? (
              <div className="mt-6 border-0 border-t border-solid border-neutral-300" />
            ) : (
              <ul className="m-0 list-none p-0 mt-6 border-0 border-t border-solid border-neutral-300 pt-2">
                {connected.map((installation) => (
                  <li
                    key={installation.installationId}
                    className="flex items-center justify-between gap-4 border-0 border-b border-solid border-neutral-300 py-4 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-brand-grey-dark">
                        {installation.accountLogin}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-neutral-900">{installation.accountType}</span>
                        {installation.suspended && <Tag color="orange">Suspended on GitHub</Tag>}
                      </div>
                    </div>
                    <Button
                      danger={true}
                      className="shrink-0"
                      disabled={disconnectMutation.isPending}
                      onClick={() => setPendingDisconnect(installation.installationId)}
                    >
                      Disconnect
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {organizationId && <RepositoryMappingTable organizationId={organizationId} />}
          </>
        )}
      </section>

      <section data-testid="slack-card" className="mt-6 rounded-lg border border-solid border-neutral-300 p-6">
        {/* Same 84px header row as the GitHub card above, so the two read as one list. */}
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border-[1.333px] border-solid border-neutral-300 bg-neutral-200">
            <SlackOutlined className="text-2xl text-brand-grey-dark" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-sm font-medium leading-5 text-brand-grey-dark">Slack</h2>
              {slackConnected && (
                <StatusLabel tone="green">
                  <CheckOutlined className="text-[12px]" />
                  <span>
                    Connected to <span className="font-semibold">{slackConnected.teamName}</span>
                  </span>
                </StatusLabel>
              )}
              {slackSettled && !slackConnected && <StatusLabel tone="neutral">Not Connected</StatusLabel>}
            </div>
            <p className="m-0 text-xs leading-4 text-brand-grey-dark">
              Send Taskonio notifications to your team as Slack direct messages.
            </p>
          </div>

          {slackSettled && !slackConnected && (
            <Button
              type="primary"
              className="shrink-0 font-semibold"
              icon={<ApiOutlined />}
              disabled={!organizationId}
              loading={connectSlackMutation.isPending}
              // Matches the GitHub call to action above - see the note on that button
              // for why the colour is inline and why this is an antd Button.
              style={
                organizationId
                  ? { backgroundColor: colorTokens.blue[600], borderColor: colorTokens.blue[600] }
                  : undefined
              }
              onClick={() => void onConnectSlack()}
            >
              Connect for your organization
            </Button>
          )}

          {slackConnected && (
            <Button
              danger={true}
              className="shrink-0"
              disabled={disconnectSlackMutation.isPending}
              onClick={() => setSlackDisconnectOpen(true)}
            >
              Disconnect
            </Button>
          )}
        </div>

        {slackInstallation.isLoading && (
          <div className="mt-6 flex justify-center">
            <Spin />
          </div>
        )}

        {slackInstallation.isError && (
          <div className="mt-6 border-0 border-t border-solid border-neutral-300 pt-6">
            <p className="m-0 text-sm text-neutral-900">
              Could not load Slack connection status for this organization. Please try again.
            </p>
            <Button className="mt-3" onClick={() => void slackInstallation.refetch()}>
              Retry
            </Button>
          </div>
        )}

        {slackConnected && (
          <div className="mt-6 border-0 border-t border-solid border-neutral-300 pt-6">
            {/* Above the health alert: an installation authorized before link unfurls
                existed holds a token without links:read/links:write, so pasted task
                links never unfurl until an admin reconnects. Reuses the same connect
                mutation as the initial "Connect for your organization" button above -
                the install upserts on organizationId, and slackInstallation.repository.ts
                documents that reconnecting the SAME workspace disconnects nobody. The
                copy says so explicitly: an admin who fears breaking their team's setup
                will otherwise simply not click. */}
            {slackConnected.needsReinstall && (
              <Alert
                className="mb-4"
                type="info"
                showIcon
                message="Reconnect to turn on link previews"
                description="This workspace was connected before this feature existed, so Slack has not granted the permission it needs. Reconnecting keeps everyone's linked accounts and their settings."
                action={
                  <Button size="small" onClick={() => void onConnectSlack()} loading={connectSlackMutation.isPending}>
                    Reconnect
                  </Button>
                }
              />
            )}
            {/* A healthy integration says nothing here: a permanent "0 failures" line
                would train an admin to stop reading the one place this number matters.
                Delivered notifications are never lost even when this fires - they stay
                in the in-app inbox and the email digest - so the description says that. */}
            {slackConnected.health.failedLast24h > 0 && (
              <Alert
                className="mb-4"
                type="warning"
                showIcon
                message={
                  slackConnected.health.failedLast24h === 1
                    ? '1 message could not be delivered in the last 24 hours.'
                    : `${slackConnected.health.failedLast24h} messages could not be delivered in the last 24 hours.`
                }
                description="Those notifications are still in the inbox and the email digest. If this keeps happening, disconnect and reconnect the workspace."
              />
            )}
            <p className="m-0 text-sm leading-relaxed text-neutral-900">
              Everyone in this organization can now link their own Slack account. Until someone links theirs, Taskonio
              has no way to address a direct message to them.{' '}
              {/* A link, not the name of a page to go find: this is the one moment an admin
                  is told the next step, and Slack notifications is a sibling route they may
                  not have noticed in the sidebar. Falls back to plain text rather than a dead
                  link if the active organization has not resolved yet. */}
              {activeOrganization?.publicId ? (
                <Link to={`/${activeOrganization.publicId}/settings/notifications`}>Link your own Slack account</Link>
              ) : (
                'You can link yours under Settings, Slack notifications.'
              )}
            </p>
          </div>
        )}
      </section>

      <Modal
        open={pendingDisconnect !== null}
        title="Disconnect GitHub?"
        okText="Yes, disconnect"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: disconnectMutation.isPending }}
        onOk={() => void onDisconnect()}
        onCancel={() => setPendingDisconnect(null)}
      >
        <p className="m-0 text-sm leading-relaxed text-neutral-900">
          Taskonio will stop receiving pull request updates for this organization. Existing links and history stay
          readable. You can reconnect later.
        </p>
      </Modal>

      <Modal
        open={slackDisconnectOpen}
        title="Disconnect Slack?"
        okText="Yes, disconnect"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: disconnectSlackMutation.isPending }}
        onOk={() => void onDisconnectSlack()}
        onCancel={() => setSlackDisconnectOpen(false)}
      >
        <p className="m-0 text-sm leading-relaxed text-neutral-900">
          Taskonio will stop sending Slack messages for this organization. Nobody has to link their Slack account again
          if you reconnect the same workspace later.
        </p>
      </Modal>
    </>
  );
}
